import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Settings, Users, FileText } from 'lucide-react';
import { ENTITY_ICONS, orgFunctionKindIconComponent } from '@/constants/entityIcons';
import { partitionEntityRefs } from '@/lib/entityRefUtils';
import { InfoHint } from '@bliks-io/ui';
import type { EntityOrSuggestionRef, EntitySuggestion } from '@bliks-io/contracts';
import { EntityPickerCell } from '@/components/entity-pickers/preview/EntityPickerCell';
import {
  EntityPickerList,
  type EntityListItem
} from '@/components/entity-pickers/preview/EntityPickerList';
import {
  useArtifactPreviewItems,
  useOrgFunctionPreviewItems,
  usePersonPreviewItems,
  useSystemPreviewItems
} from '@/components/entity-pickers/preview/useEntityPreviewItems';
import { SystemPickerModal, ArtifactPickerModal } from '@/components/entity-pickers';
import { useCreatedSuggestionSession } from '@/components/entity-pickers/hooks/useCreatedSuggestionSession';
import { usePrefetchPickerSuggestions } from '@/api/queries/usePickerSuggestions';
import {
  partitionConfirmedIds,
  toPendingId
} from '@/components/entity-pickers/shared/pendingSuggestionUtils';
import { recommendationRecordingId } from '@/components/entity-pickers/shared/recommendationScope';
import { HelpersEntityPickerModal, type HelpersSelection } from './HelpersEntityPickerModal';

export interface HelpersPickerProps {
  persons: string[];
  orgFunctions: EntityOrSuggestionRef[];
  systems: EntityOrSuggestionRef[];
  documents: string[];
  onHelpersChange: (next: HelpersSelection) => void;
  onSystemsChange: (next: EntityOrSuggestionRef[]) => void;
  onDocumentsChange: (next: string[]) => void;
  /** Process the pickers open from; drives the "for this process" bucket. */
  processId?: string;
  /**
   * Source recording id. When present, system suggest-new is enabled and the
   * created suggestion is stamped with this recording as its source
   * (`systems_used`). Absent for draft steps that aren't recordings yet.
   */
  recordingId?: string;
  /** Draft-editor host: create/link post to the draft suggestion routes. */
  draftProcessId?: string;
  /** Pending suggestions on the source node, for ghost rows + pre-checking. */
  entitySuggestions?: EntitySuggestion[];
  /**
   * Stack the systems / colleagues / documents cells vertically (one column)
   * instead of the default 3-column grid. Used by the narrow editor sidebar.
   */
  stacked?: boolean;
  /**
   * Editor opt-in: create Systems / Teams / Funktionen directly instead of
   * suggesting them.
   */
  allowCreate?: boolean;
}

type OpenPicker = null | 'systems' | 'helpers' | 'documents';

/**
 * Preview row for a suggestion-typed ref, so the cell count and the preview
 * never disagree. Falls back to a generic label when the suggestion isn't in
 * the payload (e.g. removed/rejected).
 */
function buildGhostItem(
  suggestionId: string,
  name: string | undefined,
  fallbackLabel: string,
  fallbackIcon: ReactNode
): EntityListItem {
  return {
    key: `suggestion-${suggestionId}`,
    label: name ?? fallbackLabel,
    fallbackIcon,
    isGhost: true
  };
}

/**
 * Shared helpers picker UI (systems / participants / documents): the 3-cell
 * preview grid plus the entity-picker modals, including system suggest-new.
 * The participants cell offers persons AND org functions behind one modal — a
 * helper is "either a person or a function" (plan 026, D4).
 * Renders no surrounding chrome — callers wrap it with their own
 * header/container. Used by the recording flow's `HelpersSection` and the
 * editor's `EditorHelpersSlot`, which differ only in how they source the value
 * and shape their change events.
 */
export function HelpersPicker({
  persons,
  orgFunctions: orgFunctionRefs,
  systems: systemRefs,
  documents,
  onHelpersChange,
  onSystemsChange,
  onDocumentsChange,
  processId,
  recordingId,
  draftProcessId,
  entitySuggestions,
  stacked = false,
  allowCreate
}: HelpersPickerProps) {
  const { t } = useTranslation();
  const [openPicker, setOpenPicker] = useState<OpenPicker>(null);

  const recommendationScopeId = recommendationRecordingId(recordingId, draftProcessId);

  usePrefetchPickerSuggestions(['system', 'user', 'orgFunction', 'artifact'], {
    processId,
    recordingId: recommendationScopeId
  });

  const { entityIds: systemEntityIds, suggestionIds: systemSuggestionIds } = useMemo(
    () => partitionEntityRefs(systemRefs),
    [systemRefs]
  );
  // The picker sees prefixed ids for each suggestion-typed ref so the
  // corresponding synthetic row shows as pre-checked. Confirmation partitions
  // back into entity ids and suggestion ids via `partitionConfirmedIds`.
  const systemPickerSelectedIds = useMemo(
    () => [...systemEntityIds, ...systemSuggestionIds.map(toPendingId)],
    [systemEntityIds, systemSuggestionIds]
  );

  const { entityIds: orgFunctionEntityIds, suggestionIds: orgFunctionSuggestionIds } = useMemo(
    () => partitionEntityRefs(orgFunctionRefs),
    [orgFunctionRefs]
  );

  // Ledger of suggestions created in helper picker sessions. Owned here — the
  // outermost scope that can still cancel them — and handed to the modal,
  // which tracks creates and names their rows from it.
  const session = useCreatedSuggestionSession();

  const personPreview = usePersonPreviewItems(persons);
  const orgFunctionPreview = useOrgFunctionPreviewItems(orgFunctionEntityIds);
  const systemEntityPreview = useSystemPreviewItems(systemEntityIds);
  const documentPreview = useArtifactPreviewItems(documents);

  const suggestionById = useMemo(
    () => new Map((entitySuggestions ?? []).map((s) => [s._id, s] as const)),
    [entitySuggestions]
  );

  // Ghost rows for suggestion-typed function refs. Reachable both from the
  // suggest-new rows in the modal and from an agent-written ref.
  const helperItems = useMemo<EntityListItem[]>(() => {
    const ghosts = orgFunctionSuggestionIds.map((id) => {
      const suggestion = suggestionById.get(id);
      const payload = suggestion?.kind === 'orgFunction' ? suggestion.payload : undefined;
      const KindIcon = payload
        ? orgFunctionKindIconComponent(payload.kind)
        : ENTITY_ICONS.orgFunction;
      return buildGhostItem(
        id,
        payload?.name,
        t('viralRecording.helpersHeading.pendingFunction'),
        <KindIcon className="h-3 w-3" />
      );
    });
    return [...personPreview.items, ...orgFunctionPreview.items, ...ghosts];
  }, [personPreview.items, orgFunctionPreview.items, orgFunctionSuggestionIds, suggestionById, t]);

  // Ghost preview items for suggestion-typed refs, after the resolved entities.
  const systemItems = useMemo<EntityListItem[]>(() => {
    const ghosts = systemSuggestionIds.map((id) => {
      const suggestion = suggestionById.get(id);
      const name = suggestion?.kind === 'system' ? suggestion.payload.name : undefined;
      return buildGhostItem(
        id,
        name,
        t('viralRecording.helpersHeading.pendingSystem'),
        <ENTITY_ICONS.system className="h-3 w-3" />
      );
    });
    return [...systemEntityPreview.items, ...ghosts];
  }, [systemSuggestionIds, systemEntityPreview.items, suggestionById, t]);

  /**
   * Withdraw every suggestion this picker created that `refs` no longer
   * points at. Only ever called when no helpers write is in flight — at the
   * START of a session and on a cancelled close — because the backend's
   * withdraw guard reads the CURRENT refs: firing it right after a confirm
   * would race the PATCH that removes the ref, read the doomed ref as live,
   * and leave a permanent orphan.
   */
  const settleSessionSuggestions = (refs: EntityOrSuggestionRef[]) => {
    session.withdrawUnreferenced(
      refs.filter((ref) => ref.type === 'suggestion').map((ref) => ref.id)
    );
  };

  const handleHelpersConfirm = (next: HelpersSelection) => {
    onHelpersChange(next);
    // No settle here: the write this confirm triggers is still in flight, so a
    // suggestion it just dropped is settled at the next session start instead.
    setOpenPicker(null);
  };

  const handleSystemsConfirm = (ids: string[]) => {
    const { realIds, keptSuggestionIds, addedSuggestionIds } = partitionConfirmedIds(
      ids,
      systemSuggestionIds
    );
    onSystemsChange([
      ...realIds.map((id) => ({ type: 'entity' as const, id })),
      ...keptSuggestionIds.map((id) => ({ type: 'suggestion' as const, id })),
      ...addedSuggestionIds.map((id) => ({ type: 'suggestion' as const, id }))
    ]);
    setOpenPicker(null);
  };

  // After "suggest new" creates a suggestion, append its ref so it shows up
  // alongside the resolved entities (as a checkbox row on re-open).
  const handleSystemSuggestionCreated = (suggestionId: string) => {
    onSystemsChange([...systemRefs, { type: 'suggestion', id: suggestionId }]);
    setOpenPicker(null);
  };

  return (
    <div className={`grid gap-2 items-start ${stacked ? 'grid-cols-1' : 'grid-cols-3'}`}>
      <EntityPickerCell
        icon={Settings}
        label={t('viralRecording.helpersHeading.systems', {
          count: systemRefs.length
        })}
        onClick={() => setOpenPicker('systems')}
      >
        <EntityPickerList items={systemItems} loadingCount={systemEntityPreview.loadingCount} />
      </EntityPickerCell>
      <EntityPickerCell
        icon={Users}
        label={t('viralRecording.helpersHeading.participants', {
          count: persons.length + orgFunctionRefs.length
        })}
        // Opening settles the PREVIOUS session: a suggestion an earlier
        // confirm dropped from the slot is withdrawn here, where that
        // confirm's write has long resolved. It also prunes the ledger so a
        // doc never follows the cell to another step. Residue: a drop the user
        // never returns from leaves the orphan until the next open (the
        // systems cell never withdraws at all).
        onClick={() => {
          settleSessionSuggestions(orgFunctionRefs);
          setOpenPicker('helpers');
        }}
      >
        <EntityPickerList
          items={helperItems}
          loadingCount={personPreview.loadingCount + orgFunctionPreview.loadingCount}
        />
      </EntityPickerCell>
      {/* The InfoHint is its own <button>, so it lives as a sibling of the
          interactive cell button (never nested inside it) to avoid nested
          interactive elements. It's pinned to the cell's top-right corner. */}
      <div className="relative">
        <EntityPickerCell
          icon={FileText}
          label={t('viralRecording.helpersHeading.documents', {
            count: documents.length
          })}
          onClick={() => setOpenPicker('documents')}
        >
          <EntityPickerList
            items={documentPreview.items}
            loadingCount={documentPreview.loadingCount}
          />
        </EntityPickerCell>
        <div className="absolute right-2 top-2">
          <InfoHint content={t('viralRecording.tooltips.documents')} />
        </div>
      </div>

      {/* Always-mount so Radix can play the dialog exit animation when open flips. */}
      <SystemPickerModal
        open={openPicker === 'systems'}
        onOpenChange={(o) => setOpenPicker(o ? 'systems' : null)}
        mode="multi"
        selectedIds={systemPickerSelectedIds}
        onConfirm={handleSystemsConfirm}
        onSuggestionCreated={handleSystemSuggestionCreated}
        pendingSuggestions={entitySuggestions}
        processId={processId}
        recordingId={recommendationScopeId}
        draftProcessId={draftProcessId}
        allowCreate={allowCreate}
        requestContext={
          recordingId
            ? {
                sourceStepRecordingId: recordingId,
                sourceStepRecordingPlace: 'systems_used'
              }
            : undefined
        }
      />

      <HelpersEntityPickerModal
        open={openPicker === 'helpers'}
        // The modal fires this only on cancel/escape/close, never on confirm.
        // A cancelled close persists nothing, so anything suggested in this
        // session is already an orphan and can be withdrawn right away.
        onOpenChange={(o) => {
          if (!o) settleSessionSuggestions(orgFunctionRefs);
          setOpenPicker(o ? 'helpers' : null);
        }}
        persons={persons}
        orgFunctions={orgFunctionRefs}
        onConfirm={handleHelpersConfirm}
        processId={processId}
        recordingId={recordingId}
        draftProcessId={draftProcessId}
        pendingSuggestions={entitySuggestions}
        session={session}
        allowCreate={allowCreate}
      />

      <ArtifactPickerModal
        open={openPicker === 'documents'}
        onOpenChange={(o) => setOpenPicker(o ? 'documents' : null)}
        mode="multi"
        selectedIds={documents}
        onConfirm={(ids) => {
          onDocumentsChange(ids);
          setOpenPicker(null);
        }}
        processId={processId}
        recordingId={recommendationScopeId}
      />
    </div>
  );
}
